/**
 * Core-100 CORE-BA — reply to a customer from the inbox.
 *
 * `send-sms` existed (consent check, per-shop rate limit, idempotency, durable outbound log, OCIS outcome)
 * but nothing called it, so a shop could read a customer's text and never answer it from OptiBay.
 *
 * - A reply needs a customer record linked to the conversation (consent lives on the customer); without
 *   one the composer says so instead of offering a send that would fail.
 * - One composed message = one idempotency key, so a retry or a double tap cannot text the customer twice.
 * - Refusals (no consent, a STOP, the shop's rate limit) are shown as the server states them.
 */
import { useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { edgeRefusalMessage } from '@/utils/assignment/smartAssignRefusal';

export const SMS_SEGMENT = 160;

export function smsSegments(text: string): number {
  return text.length === 0 ? 0 : Math.ceil(text.length / SMS_SEGMENT);
}

interface Props {
  shopId: string;
  customerId: string | null;
  onSent?: () => void;
}

export function InboxReplyComposer({ shopId, customerId, onSent }: Props) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);

  if (!customerId) {
    return (
      <div className="border-t p-3 text-sm text-muted-foreground" data-testid="inbox-reply-needs-customer">
        Link this conversation to a customer to reply — consent to text is recorded on the customer.
      </div>
    );
  }

  const send = async () => {
    const message = text.trim();
    if (!message || sending) return;
    if (!keyRef.current) keyRef.current = crypto.randomUUID();
    setSending(true);
    setError(null);
    const { error: fnError } = await supabase.functions.invoke('send-sms', {
      body: { shop_id: shopId, customer_id: customerId, message, idempotency_key: keyRef.current },
    });
    setSending(false);
    if (fnError) {
      setError(await edgeRefusalMessage(fnError, 'The message was not sent. Try again.'));
      return;
    }
    keyRef.current = null;
    setText('');
    onSent?.();
  };

  const segments = smsSegments(text);
  return (
    <div className="border-t p-3 space-y-2" data-testid="inbox-reply-composer">
      <Textarea
        value={text}
        onChange={(e) => { setText(e.target.value); if (!text) keyRef.current = null; }}
        placeholder="Reply to the customer by text…"
        rows={3}
        aria-label="Reply message"
      />
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {text.length} characters{segments > 1 ? ` · ${segments} texts` : ''}
        </span>
        <Button size="sm" onClick={() => void send()} disabled={sending || !text.trim()}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </div>
  );
}
